/**
 * InterceptedFetchApi
 * ================================
 * Global response interceptor (FetchAPI level):
 *   - 401: Clear identity → Redirect to /?reason=session-expired
 *   - 403: Show permission-denied toast with its error code (no redirect) + Log
 *   - Loop guard: Skip redirect on session-check endpoint 401
 *   - Loop guard: Skip redirect on 401 from Backstage plugin backends
 *
 * Session endpoint read from app-config.yaml: ses.authApi.endpoints.session
 */

import i18n from 'i18next';
import { ErrorCodes, readErrorCode, withErrorCode } from './errorCodes';
import type {
  ConfigApi,
  DiscoveryApi,
  ErrorApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import {
  NO_ACCESS_PATH,
  isNoOrganization,
  onNoAccessPage,
} from '../utils/noAccess';

export type InterceptedFetchApiOptions = {
  configApi: ConfigApi;
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
  errorApi: ErrorApi;
  getAccessToken?: () => string | null;
  clearAccessToken?: () => void;
};

type BaseFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

const SESSION_EXPIRED_LOCATION = '/?reason=session-expired';

/**
 * Backstage plugin backends authenticate with their own token, not the SES
 * session cookie, so a 401 from them says nothing about the user's SES
 * session. Treating it as session expiry signs the user out and, with
 * return_to, sends them straight back to the page that fails again - an
 * endless redirect on /catalog and /api-docs.
 */
const BACKSTAGE_PLUGIN_API_PREFIXES = [
  '/api/catalog',
  '/api/search',
  '/api/techdocs',
  '/api/scaffolder',
];

export class InterceptedFetchApi implements FetchApi {
  private readonly baseFetch: BaseFetch;

  private readonly _configApi: ConfigApi;
  private readonly _discoveryApi: DiscoveryApi;
  private readonly _identityApi: IdentityApi;
  private readonly _errorApi: ErrorApi;
  private readonly getAccessToken?: () => string | null;
  private readonly clearAccessToken?: () => void;

  /**
   * Path of the session/boot auth-check endpoint (e.g. "/auth/session").
   * A 401 from THIS endpoint means "not logged in yet" during boot - routing
   * already shows the login page, so we must NOT redirect on it (loop guard).
   */
  private readonly sessionEndpoint: string;
  private readonly backendBaseUrl: string;

  /** Loop guard: ensures only one redirect is ever kicked off. */
  private isRedirecting = false;

  constructor(opts: InterceptedFetchApiOptions) {
    this._configApi = opts.configApi;
    this._discoveryApi = opts.discoveryApi;
    this._identityApi = opts.identityApi;
    this._errorApi = opts.errorApi;
    this.getAccessToken = opts.getAccessToken;
    this.clearAccessToken = opts.clearAccessToken;

    this.sessionEndpoint =
      opts.configApi.getOptionalString('ses.authApi.endpoints.session') ?? '';
    this.backendBaseUrl =
      opts.configApi.getOptionalString('backend.baseUrl') ?? '';

    this.baseFetch = (input, init) => window.fetch(input, init);

    void this._configApi;
    void this._discoveryApi;
  }

  async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url = this.toUrl(input);
    const authInit = this.withAuthorizationIfNeeded(url, init);
    const headers = new Headers(authInit?.headers);

    // Inject Backstage identity token so Catalog/Org APIs receive valid
    // credentials. InterceptedFetchApi replaces the standard fetchApiRef which
    // normally applies IdentityAuthInjectorFetchMiddleware — without this,
    // every Backstage backend call would 401.
    if (!headers.get('authorization') && this.isInternalRequest(url)) {
      try {
        const { token } = await this._identityApi.getCredentials();
        if (token) {
          headers.set('authorization', `Bearer ${token}`);
        }
      } catch {
        // Not signed in yet — proceed without token; 401 handler will redirect.
      }
    }

    const mergedInit: RequestInit = { ...authInit, headers };
    const response = await this.baseFetch(input, mergedInit);

    if (response.status === 401) {
      this.handle401(input);
    } else if (await isNoOrganization(response)) {
      this.handleNoOrganization();
    } else if (response.status === 403) {
      void this.handle403(input, mergedInit, response);
    }
    return response;
  }

  // ----------------------------------------------------------------
  // 401 - session expired: clear identity, redirect to login.
  // ----------------------------------------------------------------
  private handle401(input: RequestInfo | URL): void {
    const url = this.toUrl(input);

    // Loop prevention:
    // 1. Boot-time session-check 401 = "not logged in" - routing handles it.
    // 2. Already on the login screen with the reason flag set.
    // 3. A redirect is already in flight.
    // 4. A Backstage plugin backend rejected its own token, not our session.
    if (
      this.isSessionCheckCall(url) ||
      this.isBackstagePluginCall(url) ||
      this.isAlreadyOnSessionExpiredScreen() ||
      this.isRedirecting
    ) {
      return;
    }

    this.isRedirecting = true;

    // httpOnly cookie already invalidated server-side; nothing to clear in JS.
    // Best-effort token clear, then identity clear; never block redirect.
    this.safeClearAccessToken();
    this.safeSignOut();

    this.redirectTo(SESSION_EXPIRED_LOCATION);
  }

  // ----------------------------------------------------------------
  // 403 NO_ORGANIZATION - signed in, but no organization: show the no-access
  // page. A login would succeed again, so the 401 path would loop.
  // ----------------------------------------------------------------
  private handleNoOrganization(): void {
    if (onNoAccessPage() || this.isRedirecting) {
      return;
    }
    this.isRedirecting = true;
    this.redirectTo(NO_ACCESS_PATH);
  }

  // ----------------------------------------------------------------
  // 403 - permission denied: toast + log, stay on page.
  // ----------------------------------------------------------------
  private async handle403(
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    response: Response,
  ): Promise<void> {
    const url = this.toUrl(input);
    const method = (init?.method ?? 'GET').toUpperCase();

    // eslint-disable-next-line no-console
    console.warn(`[403] Permission denied: ${method} ${url}`);

    // A 403 from the account checks (e.g. disabled user, 12001) has its own
    // message; anything else is a plain permission error (CB25SES-1804).
    const code = (await readErrorCode(response)) ?? ErrorCodes.forbidden;
    const message = code.startsWith('12')
      ? i18n.t(`auth:signInError.${code}`)
      : i18n.t('auth:accessDenied');
    const error = new Error(withErrorCode(message, code));
    error.name = 'AccessDenied';
    try {
      this._errorApi.post(error);
    } catch {
      // Never let notification failures break the fetch contract.
    }
  }

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------
  private safeSignOut(): void {
    try {
      const result = this._identityApi.signOut();
      void Promise.resolve(result).catch(() => {});
    } catch {
      // Never let signOut failures block the redirect path.
    }
  }

  private safeClearAccessToken(): void {
    if (!this.clearAccessToken) {
      return;
    }

    try {
      this.clearAccessToken();
    } catch {
      // Never let token-clear failures block the redirect path.
    }
  }

  private withAuthorizationIfNeeded(
    url: string,
    init?: RequestInit,
  ): RequestInit | undefined {
    const token = this.getAccessToken?.();
    if (!token || !this.isInternalRequest(url)) {
      return init;
    }

    const headers = new Headers(init?.headers);
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return {
      ...init,
      headers,
    };
  }

  private toUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.toString();
    return input.url;
  }

  private isSessionCheckCall(url: string): boolean {
    if (this.sessionEndpoint.length === 0) {
      return false;
    }

    return (
      this.normalizePathname(this.toPathname(url)) ===
      this.normalizePathname(this.toPathname(this.sessionEndpoint))
    );
  }

  private isBackstagePluginCall(url: string): boolean {
    const pathname = this.normalizePathname(this.toPathname(url));
    return BACKSTAGE_PLUGIN_API_PREFIXES.some(
      prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  }

  private toPathname(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url.split(/[?#]/, 1)[0];
    }
  }

  private normalizePathname(pathname: string): string {
    if (pathname === '/') {
      return pathname;
    }

    return pathname.replace(/\/+$/, '');
  }

  private isInternalRequest(url: string): boolean {
    const absolute = this.tryParseAbsoluteUrl(url);
    if (!absolute) {
      const pathname = this.toPathname(url);
      return pathname.startsWith('/');
    }

    if (absolute.origin === window.location.origin) {
      return true;
    }

    const backendBase = this.tryParseAbsoluteUrl(this.backendBaseUrl);
    if (backendBase && absolute.origin === backendBase.origin) {
      return true;
    }

    return false;
  }

  private tryParseAbsoluteUrl(url: string): URL | null {
    try {
      return new URL(url);
    } catch {
      return null;
    }
  }

  private isAlreadyOnSessionExpiredScreen(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.location.search.includes('reason=session-expired')
    );
  }

  /**
   * Redirect seam. Isolated so it's the single place navigation happens,
   * which also makes it spyable in tests (jsdom locks window.location).
   */
  private redirectTo(location: string): void {
    window.location.assign(location);
  }
}
